import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import KpiCard from '../../components/KpiCard';
import Header from '../../components/Header';
import { styles } from '../../styles/theme';
import useCachedQuery from '../../hooks/useCachedQuery';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { searchMatchPre } from '../../utils/searchMatch';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001';

const getAuthHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const StatusBadge = ({ status }) => {
  const style = styles.statusColors[status] || { bg: '#e9ecef', color: '#495057' };
  return (
    <span style={{
      display: 'inline-block',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 'bold',
      background: style.bg,
      color: style.color
    }}>
      {status}
    </span>
  );
};

const DepoDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [message] = useState(''); // eslint-disable-line no-unused-vars
  const [arama, setArama] = useState('');
  const debouncedArama = useDebouncedValue(arama, 300);
  const userCacheKey = user?.id || user?.email || user?.name || 'anon';

  const {
    data: siparisler = [],
    isLoading,
    isValidating
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'depo', 'siparisler'],
    async () => {
      const res = await fetch(`${API_URL}/api/siparisler`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 20000 }
  );

  const {
    data: dashSummary = {},
    isLoading: summaryLoading
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'depo', 'summary'],
    async () => {
      const res = await fetch(`${API_URL}/api/dashboard/summary`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return data && typeof data === 'object' ? data : {};
    },
    { staleTime: 20000 }
  );

  const {
    data: grnList = [],
    isLoading: grnLoading
  } = useCachedQuery(
    ['dashboard', userCacheKey, 'depo', 'grn'],
    async () => {
      const res = await fetch(`${API_URL}/api/grn`, { headers: getAuthHeaders() });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      return Array.isArray(data) ? data : [];
    },
    { staleTime: 20000 }
  );

  const recentGrn = useMemo(() => {
    return [...grnList]
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 5);
  }, [grnList]);

  const pendingGrnCount = useMemo(() => {
    return grnList.filter(g => g.durum === 'BEKLEMEDE' || g.durum === 'KALITE_KONTROL').length;
  }, [grnList]);

  const yoldaSiparişler = useMemo(() => siparisler.filter(s => s.durum === 'YOLDA'), [siparisler]);

  const filteredYoldaSiparişler = useMemo(() => {
    const q = (debouncedArama || '').trim().toLowerCase();
    if (!q) return yoldaSiparişler;
    return yoldaSiparişler.filter(s =>
      searchMatchPre(s.siparis_no, q) ||
      searchMatchPre(s.tedarikçi, q)
    );
  }, [yoldaSiparişler, debouncedArama]);

  return (
    <div>
      <Header title="Depo Yonetim Panosu" />
      <div style={{ padding: '20px', background: '#f5f5f5', minHeight: 'calc(100vh - 60px)' }}>
        {message && (
          <div style={{
            padding: '15px',
            marginBottom: '20px',
            background: message.includes('başarılı') || message.includes('tamamlandı') ? '#d4edda' : '#f8d7da',
            color: message.includes('başarılı') || message.includes('tamamlandı') ? '#155724' : '#721c24',
            borderRadius: '6px',
            border: `1px solid ${message.includes('başarılı') || message.includes('tamamlandı') ? '#c3e6cb' : '#f5c6cb'}`
          }}>
            {message}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
          <KpiCard title="Teslim Bekleyen" value={isLoading ? '...' : `${yoldaSiparişler.length} siparis`} color={styles.success} icon="package" />
          <KpiCard title="Bekleyen GRN" value={grnLoading ? '...' : `${pendingGrnCount} adet`} color={styles.warning} icon="check" />
          <KpiCard title="Kritik Stok" value={summaryLoading ? '...' : `${dashSummary.stok?.kritik_sayi || 0} urun`} color={(dashSummary.stok?.kritik_sayi || 0) > 0 ? styles.danger : styles.success} icon="chart" />
          <KpiCard title="Dusuk Stok" value={summaryLoading ? '...' : `${dashSummary.stok?.dusuk_sayi || 0} urun`} color={(dashSummary.stok?.dusuk_sayi || 0) > 0 ? styles.warning : styles.info} icon="clock" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '20px', marginBottom: '20px' }}>
          <div style={{ background: 'white', borderRadius: '8px', padding: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px', gap: '12px', flexWrap: 'wrap' }}>
              <h3 style={{ margin: 0, color: styles.secondary }}>Teslim Bekleyen Siparişler</h3>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <input
                  value={arama}
                  onChange={(e) => setArama(e.target.value)}
                  placeholder="Sipariş no / tedarikçi ara"
                  style={{ width: '240px', maxWidth: '100%', border: '1px solid #ddd', borderRadius: '6px', padding: '8px 10px', fontSize: '13px' }}
                />
                {isValidating && !isLoading && (
                  <span style={{ fontSize: '12px', color: '#666', background: '#f1f3f5', borderRadius: '12px', padding: '4px 10px' }}>
                    Güncelleniyor...
                  </span>
                )}
              </div>
            </div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8f9fa' }}>
                  <th style={{ padding: '7px', textAlign: 'left', fontSize: '13px' }}>Sipariş No</th>
                  <th style={{ padding: '7px', textAlign: 'left', fontSize: '13px' }}>Tedarikçi</th>
                  <th style={{ padding: '7px', textAlign: 'right', fontSize: '13px' }}>Toplam</th>
                  <th style={{ padding: '7px', textAlign: 'left', fontSize: '13px' }}>Durum</th>
                  <th style={{ padding: '7px', textAlign: 'center', fontSize: '13px' }}>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  Array.from({ length: 5 }).map((_, row) => (
                    <tr key={`skeleton-${row}`} style={{ borderBottom: '1px solid #eee' }}>
                      {Array.from({ length: 5 }).map((__, col) => (
                        <td key={`skeleton-${row}-${col}`} style={{ padding: '10px' }}>
                          <div style={{ height: '12px', borderRadius: '4px', background: '#e9ecef', width: col === 1 ? '85%' : '65%' }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : filteredYoldaSiparişler.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                      Teslim bekleyen siparis yok
                    </td>
                  </tr>
                ) : (
                  filteredYoldaSiparişler.map(siparis => (
                    <tr key={siparis.id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px', fontWeight: 'bold', fontSize: '12px' }}>{siparis.siparis_no}</td>
                      <td style={{ padding: '10px' }}>{siparis.tedarikçi}</td>
                      <td style={{ padding: '7px', textAlign: 'right' }}>{((siparis.toplam || siparis.tutar || 0)).toLocaleString('tr-TR')} TL</td>
                      <td style={{ padding: '10px' }}><StatusBadge status={siparis.durum} /></td>
                      <td style={{ padding: '7px', textAlign: 'center' }}>
                        <button
                          onClick={() => navigate('/grn/olustur')}
                          style={{
                            padding: '6px 12px',
                            background: styles.primary,
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: 'bold'
                          }}
                        >
                          Teslim Al
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div style={{ background: 'white', borderRadius: '8px', padding: '20px' }}>
            <h3 style={{ margin: '0 0 15px 0', color: styles.secondary }}>Hızlı Islemler</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button onClick={() => navigate('/grn/olustur')} style={{ padding: '15px', background: styles.primary, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', textAlign: 'left' }}>
                Teslim Alma (GRN)
              </button>
              <button onClick={() => navigate('/grn/listesi')} style={{ padding: '15px', background: styles.secondary, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', textAlign: 'left' }}>
                GRN Listesi
              </button>
              <button onClick={() => navigate('/kalite-kontrol')} style={{ padding: '15px', background: styles.success, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', textAlign: 'left' }}>
                Kalite Kontrol
              </button>
              <button onClick={() => navigate('/stok/durum')} style={{ padding: '15px', background: styles.info, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', textAlign: 'left' }}>
                Stok Durumu
              </button>
            </div>
          </div>
        </div>

        <div style={{ background: 'white', borderRadius: '8px', padding: '20px' }}>
          <h3 style={{ margin: '0 0 15px 0', color: styles.secondary }}>Son Mal Kabul Fisleri (GRN)</h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#f8f9fa' }}>
                <th style={{ padding: '7px', textAlign: 'left', fontSize: '13px' }}>GRN No</th>
                <th style={{ padding: '7px', textAlign: 'left', fontSize: '13px' }}>Sipariş</th>
                <th style={{ padding: '7px', textAlign: 'left', fontSize: '13px' }}>Tedarikçi</th>
                <th style={{ padding: '7px', textAlign: 'left', fontSize: '13px' }}>Teslim Tarihi</th>
                <th style={{ padding: '7px', textAlign: 'right', fontSize: '13px' }}>Miktar</th>
                <th style={{ padding: '7px', textAlign: 'right', fontSize: '13px' }}>Fark</th>
                <th style={{ padding: '7px', textAlign: 'left', fontSize: '13px' }}>Durum</th>
              </tr>
            </thead>
            <tbody>
              {grnLoading ? (
                Array.from({ length: 3 }).map((_, row) => (
                  <tr key={`grn-skeleton-${row}`} style={{ borderBottom: '1px solid #eee' }}>
                    {Array.from({ length: 7 }).map((__, col) => (
                      <td key={`grn-skeleton-${row}-${col}`} style={{ padding: '10px' }}>
                        <div style={{ height: '12px', borderRadius: '4px', background: '#e9ecef', width: col === 2 ? '85%' : '65%' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : recentGrn.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    Henuz GRN kaydi yok. "Teslim Alma" butonuna tiklayarak ilk kaydinizi olusturun.
                  </td>
                </tr>
              ) : (
                recentGrn.map(grn => (
                  <tr key={grn.id} style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }} onClick={() => navigate(`/grn/${grn.id}`)}>
                    <td style={{ padding: '10px', fontWeight: 'bold', fontSize: '12px' }}>{grn.grn_no}</td>
                    <td style={{ padding: '10px', fontSize: '13px' }}>{grn.siparis_no || '-'}</td>
                    <td style={{ padding: '10px', fontSize: '13px' }}>{grn.tedarikci || '-'}</td>
                    <td style={{ padding: '10px', fontSize: '13px' }}>{grn.created_at ? new Date(grn.created_at).toLocaleDateString('tr-TR') : '-'}</td>
                    <td style={{ padding: '7px', textAlign: 'right', fontSize: '13px' }}>{grn.toplam_kalem || '-'} kalem</td>
                    <td style={{ padding: '7px', textAlign: 'right', fontSize: '13px' }}>{grn.lokasyon_ad || '-'}</td>
                    <td style={{ padding: '10px' }}><StatusBadge status={grn.durum} /></td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DepoDashboard;
